/**
 * Analytics Data Hooks
 *
 * React Query hooks for fetching real analytics data from the database
 * Used by the admin analytics dashboard to display actual metrics
 */

import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/lib/supabase";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AnalyticsMetrics {
  totalPageViews: number;
  uniqueSessions: number;
  totalConversions: number;
  conversionRate: number;
}

export interface ConversionSource {
  source: string;
  conversions: number;
  percentage: number;
}

export interface ToolUsageStat {
  tool_name: string;
  total_uses: number;
  avg_duration_seconds: number;
}

export interface SessionMetric {
  date: string;
  sessions: number;
  page_views: number;
  avg_duration_seconds: number;
}

export interface TimeRange {
  start: Date;
  end: Date;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get time range for common periods
 */
export function getTimeRange(period: "7d" | "30d" | "90d"): TimeRange {
  const end = new Date();
  const start = new Date();

  switch (period) {
    case "7d":
      start.setDate(start.getDate() - 7);
      break;
    case "30d":
      start.setDate(start.getDate() - 30);
      break;
    case "90d":
      start.setDate(start.getDate() - 90);
      break;
  }

  return { start, end };
}

// ============================================================================
// ANALYTICS HOOKS
// ============================================================================

/**
 * Fetch overview metrics (page views, sessions, conversions, conversion rate)
 */
export function useAnalyticsMetrics(timeRange: TimeRange) {
  return useQuery({
    queryKey: ["analytics", "metrics", timeRange.start, timeRange.end],
    queryFn: async (): Promise<AnalyticsMetrics> => {
      // Get total page views
      const { count: pageViews } = await supabase
        .from("analytics_events")
        .select("*", { count: "exact", head: true })
        .eq("event_type", "page_view")
        .gte("created_at", timeRange.start.toISOString())
        .lte("created_at", timeRange.end.toISOString());

      // Get unique sessions
      const { data: sessions } = await supabase
        .from("analytics_sessions")
        .select("session_id")
        .gte("started_at", timeRange.start.toISOString())
        .lte("started_at", timeRange.end.toISOString());

      // Get total conversions
      const { count: conversions } = await supabase
        .from("analytics_conversions")
        .select("*", { count: "exact", head: true })
        .gte("created_at", timeRange.start.toISOString())
        .lte("created_at", timeRange.end.toISOString());

      const uniqueSessions = sessions?.length || 0;
      const totalConversions = conversions || 0;
      const conversionRate =
        uniqueSessions > 0 ? (totalConversions / uniqueSessions) * 100 : 0;

      return {
        totalPageViews: pageViews || 0,
        uniqueSessions,
        totalConversions,
        conversionRate: Math.round(conversionRate * 100) / 100,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Fetch conversion sources breakdown
 */
export function useConversionSources(timeRange: TimeRange) {
  return useQuery({
    queryKey: [
      "analytics",
      "conversions",
      "sources",
      timeRange.start,
      timeRange.end,
    ],
    queryFn: async (): Promise<ConversionSource[]> => {
      const { data, error } = await supabase
        .from("analytics_conversions")
        .select("source")
        .gte("created_at", timeRange.start.toISOString())
        .lte("created_at", timeRange.end.toISOString());

      if (error) throw error;

      // Count conversions by source
      const sourceCounts = (data || []).reduce(
        (acc, item) => {
          acc[item.source] = (acc[item.source] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      const total = Object.values(sourceCounts).reduce(
        (sum, count) => sum + count,
        0,
      );

      // Convert to array with percentages
      return Object.entries(sourceCounts).map(([source, conversions]) => ({
        source,
        conversions,
        percentage:
          total > 0 ? Math.round((conversions / total) * 100 * 100) / 100 : 0,
      }));
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Fetch tool usage statistics
 */
export function useToolUsageStats(timeRange: TimeRange) {
  return useQuery({
    queryKey: ["analytics", "tools", "usage", timeRange.start, timeRange.end],
    queryFn: async (): Promise<ToolUsageStat[]> => {
      const { data, error } = await supabase
        .from("analytics_tool_usage")
        .select("tool_name, session_duration")
        .gte("created_at", timeRange.start.toISOString())
        .lte("created_at", timeRange.end.toISOString());

      if (error) throw error;

      // Group by tool name
      const toolStats = (data || []).reduce(
        (acc, item) => {
          if (!acc[item.tool_name]) {
            acc[item.tool_name] = {
              tool_name: item.tool_name,
              total_uses: 0,
              total_duration_ms: 0,
            };
          }

          acc[item.tool_name].total_uses += 1;

          // Parse interval to milliseconds if available
          if (item.session_duration) {
            // Simple interval parsing (format: "HH:MM:SS" or similar)
            const durationMs = parseDuration(item.session_duration);
            acc[item.tool_name].total_duration_ms += durationMs;
          }

          return acc;
        },
        {} as Record<
          string,
          { tool_name: string; total_uses: number; total_duration_ms: number }
        >,
      );

      // Convert to array with average duration in seconds
      return Object.values(toolStats).map((stat) => ({
        tool_name: stat.tool_name,
        total_uses: stat.total_uses,
        avg_duration_seconds:
          stat.total_uses > 0
            ? Math.round(stat.total_duration_ms / stat.total_uses / 1000)
            : 0,
      }));
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Fetch session metrics over time (for trend charts)
 */
export function useSessionMetrics(timeRange: TimeRange) {
  return useQuery({
    queryKey: [
      "analytics",
      "sessions",
      "metrics",
      timeRange.start,
      timeRange.end,
    ],
    queryFn: async (): Promise<SessionMetric[]> => {
      const { data, error } = await supabase
        .from("analytics_sessions")
        .select("started_at, page_views, duration")
        .gte("started_at", timeRange.start.toISOString())
        .lte("started_at", timeRange.end.toISOString())
        .order("started_at", { ascending: true });

      if (error) throw error;

      // Group by date
      const dailyMetrics = (data || []).reduce(
        (acc, session) => {
          const date = new Date(session.started_at).toISOString().split("T")[0];

          if (!acc[date]) {
            acc[date] = {
              date,
              sessions: 0,
              page_views: 0,
              total_duration_ms: 0,
            };
          }

          acc[date].sessions += 1;
          acc[date].page_views += session.page_views || 0;

          if (session.duration) {
            acc[date].total_duration_ms += parseDuration(session.duration);
          }

          return acc;
        },
        {} as Record<
          string,
          {
            date: string;
            sessions: number;
            page_views: number;
            total_duration_ms: number;
          }
        >,
      );

      // Convert to array with average duration
      return Object.values(dailyMetrics).map((metric) => ({
        date: metric.date,
        sessions: metric.sessions,
        page_views: metric.page_views,
        avg_duration_seconds:
          metric.sessions > 0
            ? Math.round(metric.total_duration_ms / metric.sessions / 1000)
            : 0,
      }));
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Get conversion rate from database function
 */
export function useConversionRate(timeRange: TimeRange) {
  return useQuery({
    queryKey: ["analytics", "conversion-rate", timeRange.start, timeRange.end],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_conversion_rate", {
        start_date: timeRange.start.toISOString(),
        end_date: timeRange.end.toISOString(),
      });

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Get total tool interactions count
 */
export function useToolInteractions(timeRange: TimeRange) {
  return useQuery({
    queryKey: [
      "analytics",
      "tool-interactions",
      timeRange.start,
      timeRange.end,
    ],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("analytics_tool_usage")
        .select("*", { count: "exact", head: true })
        .gte("created_at", timeRange.start.toISOString())
        .lte("created_at", timeRange.end.toISOString());

      if (error) throw error;
      return count || 0;
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse PostgreSQL interval to milliseconds
 * Handles formats like "00:05:30" (HH:MM:SS) or "1 day 02:30:00"
 */
function parseDuration(interval: string): number {
  if (!interval) return 0;

  // Simple time format: HH:MM:SS or MM:SS
  const timeMatch = interval.match(/(\d{1,2}):(\d{2}):(\d{2})/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const seconds = parseInt(timeMatch[3], 10);
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }

  // Try to extract time components from more complex formats
  let totalMs = 0;

  // Days
  const daysMatch = interval.match(/(\d+)\s+days?/);
  if (daysMatch) {
    totalMs += parseInt(daysMatch[1], 10) * 24 * 3600 * 1000;
  }

  // Hours
  const hoursMatch = interval.match(/(\d+)\s+hours?/);
  if (hoursMatch) {
    totalMs += parseInt(hoursMatch[1], 10) * 3600 * 1000;
  }

  // Minutes
  const minutesMatch = interval.match(/(\d+)\s+mins?/);
  if (minutesMatch) {
    totalMs += parseInt(minutesMatch[1], 10) * 60 * 1000;
  }

  // Seconds
  const secondsMatch = interval.match(/(\d+)\s+secs?/);
  if (secondsMatch) {
    totalMs += parseInt(secondsMatch[1], 10) * 1000;
  }

  return totalMs;
}
